import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, X, Image as ImageIcon, Video, FileText,
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon,
  AlignLeft, AlignCenter, AlignRight, Upload, Trash2, GripVertical,
  Eye, Sparkles, Type
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';
import { sanitizeHTML } from '@/utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Gallery Image Uploader Component
function GalleryImageUploader({ onAdd, folder }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const fileInputRef = useRef(null);

  const uploadSingleFile = async (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) return { error: `${file.name}: Invalid type` };
    if (file.size > 5 * 1024 * 1024) return { error: `${file.name}: Too large (max 5MB)` };

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
    if (res.ok) {
      const data = await res.json();
      let imageUrl = data.url;
      if (imageUrl && imageUrl.startsWith('http://')) imageUrl = imageUrl.replace('http://', 'https://');
      return { url: imageUrl };
    }
    return { error: `${file.name}: Upload failed` };
  };

  const handleMultiFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    setUploading(true);
    setUploadProgress(fileArray.map(f => ({ name: f.name, status: 'pending' })));

    let successCount = 0;
    for (let i = 0; i < fileArray.length; i++) {
      setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, status: 'uploading' } : p));
      const result = await uploadSingleFile(fileArray[i]);
      if (result.url) {
        onAdd(result.url);
        successCount++;
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, status: 'done' } : p));
      } else {
        toast.error(result.error);
        setUploadProgress(prev => prev.map((p, j) => j === i ? { ...p, status: 'error' } : p));
      }
    }

    toast.success(`${successCount}/${fileArray.length} images uploaded`);
    setUploading(false);
    setTimeout(() => setUploadProgress([]), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleMultiFileSelect(e.target.files)}
          className="hidden"
          data-testid="gallery-file-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          data-testid="gallery-upload-btn"
        >
          {uploading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload size={18} />
          )}
          {uploading ? 'Uploading...' : 'Upload Images (Multi-select)'}
        </button>
        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Or paste URL
        </button>
      </div>
      {showUrlInput && (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            type="button"
            onClick={() => {
              if (urlValue.trim()) {
                onAdd(urlValue.trim());
                setUrlValue('');
                setShowUrlInput(false);
              }
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Add
          </button>
        </div>
      )}
      {uploadProgress.length > 0 && (
        <div className="space-y-1 mt-2">
          {uploadProgress.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 rounded-full ${
                p.status === 'done' ? 'bg-green-500' : p.status === 'uploading' ? 'bg-blue-500 animate-pulse' : p.status === 'error' ? 'bg-red-500' : 'bg-gray-300'
              }`} />
              <span className="text-gray-600 truncate">{p.name}</span>
              <span className="text-gray-400 capitalize">{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const editorRef = useRef(null);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaType, setMediaType] = useState('image');
  
  const [product, setProduct] = useState({
    name: '',
    brand: '',
    capacity: '',
    technology: 'RO+UV+UF',
    price: '',
    originalPrice: '',
    image: '',
    images: [],
    media: [],
    rich_description: '',
    features: [''],
    specifications: {
      purification: '',
      capacity: '',
      warranty: '1 Year',
      powerConsumption: '',
      storageType: '',
      dimensions: '',
      weight: '',
      installation: 'Free'
    },
    inStock: true,
    stock_quantity: 100,
    description: '',
    category_id: '',
    tags: [],
    sku: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: []
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/categories`);
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${id}`);
      if (res.ok) {
        const data = await res.json();
        setProduct({
          ...data,
          features: data.features?.length ? data.features : [''],
          specifications: { ...product.specifications, ...data.specifications }
        });
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...product,
        price: parseFloat(product.price) || 0,
        originalPrice: parseFloat(product.originalPrice) || parseFloat(product.price) || 0,
        stock_quantity: parseInt(product.stock_quantity) || 100,
        features: product.features.filter(f => f.trim()),
        tags: typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()) : product.tags,
        meta_keywords: typeof product.meta_keywords === 'string' ? product.meta_keywords.split(',').map(t => t.trim()) : product.meta_keywords
      };

      const url = isEditing 
        ? `${BACKEND_URL}/api/products/${id}`
        : `${BACKEND_URL}/api/products`;
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(isEditing ? 'Product updated successfully!' : 'Product created successfully!');
        navigate('/admin/products');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setProduct(prev => ({ ...prev, [field]: value }));
  };

  const updateSpecification = (field, value) => {
    setProduct(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [field]: value }
    }));
  };

  const addFeature = () => {
    setProduct(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...product.features];
    newFeatures[index] = value;
    setProduct(prev => ({ ...prev, features: newFeatures }));
  };

  const removeFeature = (index) => {
    setProduct(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addGalleryImage = (url) => {
    if (url) {
      setProduct(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  const removeGalleryImage = (index) => {
    setProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addMedia = (type, url) => {
    if (url) {
      setProduct(prev => ({
        ...prev,
        media: [...prev.media, { type, url, order: prev.media.length }]
      }));
      setShowMediaModal(false);
    }
  };

  const removeMedia = (index) => {
    setProduct(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      updateField('rich_description', editorRef.current.innerHTML);
    }
  };

  const insertMedia = (html) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, html);
      updateField('rich_description', editorRef.current.innerHTML);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      insertMedia(`<img src="${url}" alt="Product image" style="max-width: 100%; height: auto; margin: 10px 0; border-radius: 8px;" />`);
    }
  };

  const insertVideo = () => {
    const url = prompt('Enter YouTube or video URL:');
    if (url) {
      // Convert YouTube URL to embed
      let embedUrl = url;
      if (url.includes('youtube.com/watch')) {
        const videoId = url.split('v=')[1]?.split('&')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
      insertMedia(`<div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 20px 0;"><iframe src="${embedUrl}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;" allowfullscreen></iframe></div>`);
    }
  };

  const insertGif = () => {
    const url = prompt('Enter GIF URL:');
    if (url) {
      insertMedia(`<img src="${url}" alt="Product GIF" style="max-width: 100%; height: auto; margin: 10px 0; border-radius: 8px;" />`);
    }
  };

  const insertCallout = () => {
    insertMedia(`<div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 12px; margin: 20px 0; text-align: center;"><h3 style="margin: 0 0 10px 0; font-size: 1.2em;">✨ Special Feature</h3><p style="margin: 0;">Add your highlight text here</p></div>`);
  };

  const insertFeatureBox = () => {
    insertMedia(`<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 20px 0;">
      <div style="background: #f0f9ff; padding: 15px; border-radius: 10px; text-align: center;">
        <div style="font-size: 2em;">💧</div>
        <h4 style="margin: 10px 0 5px; color: #0369a1;">Pure Water</h4>
        <p style="margin: 0; font-size: 0.9em; color: #64748b;">Description here</p>
      </div>
      <div style="background: #f0fdf4; padding: 15px; border-radius: 10px; text-align: center;">
        <div style="font-size: 2em;">⚡</div>
        <h4 style="margin: 10px 0 5px; color: #15803d;">Energy Efficient</h4>
        <p style="margin: 0; font-size: 0.9em; color: #64748b;">Description here</p>
      </div>
    </div>`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-gray-600">Fill in the product details below</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/products')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <Save size={20} />
            )}
            {saving ? 'Saving...' : 'Save Product'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b">
          <nav className="flex gap-4 px-6">
            {[
              { id: 'basic', label: 'Basic Info' },
              { id: 'media', label: 'Images & Media' },
              { id: 'description', label: 'Rich Description' },
              { id: 'specs', label: 'Specifications' },
              { id: 'seo', label: 'SEO' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Kent Grand Plus RO Water Purifier"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand *</label>
                  <input
                    type="text"
                    value={product.brand}
                    onChange={(e) => updateField('brand', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Kent"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={product.category_id}
                    onChange={(e) => updateField('category_id', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capacity</label>
                  <input
                    type="text"
                    value={product.capacity}
                    onChange={(e) => updateField('capacity', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 8L"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Technology</label>
                  <select
                    value={product.technology}
                    onChange={(e) => updateField('technology', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="RO">RO</option>
                    <option value="RO+UV">RO + UV</option>
                    <option value="RO+UV+UF">RO + UV + UF</option>
                    <option value="RO+UV+UF+TDS">RO + UV + UF + TDS</option>
                    <option value="UV">UV Only</option>
                    <option value="UF">UF Only</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price (₹) *</label>
                  <input
                    type="number"
                    value={product.price}
                    onChange={(e) => updateField('price', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="12999"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">MRP (₹)</label>
                  <input
                    type="number"
                    value={product.originalPrice}
                    onChange={(e) => updateField('originalPrice', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="15999"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity</label>
                  <input
                    type="number"
                    value={product.stock_quantity}
                    onChange={(e) => updateField('stock_quantity', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
                <textarea
                  value={product.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="Brief description of the product..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Key Features</label>
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add Feature
                  </button>
                </div>
                <div className="space-y-2">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Feature ${index + 1}`}
                      />
                      {product.features.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={product.inStock}
                    onChange={(e) => updateField('inStock', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="font-medium text-gray-700">In Stock</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={product.show_on_homepage || false}
                    onChange={(e) => updateField('show_on_homepage', e.target.checked)}
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    data-testid="product-show-on-homepage"
                  />
                  <span className="font-medium text-gray-700">Show on Homepage</span>
                </label>
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div>
                <ImageUploader
                  value={product.image}
                  onChange={(url) => updateField('image', url)}
                  folder="products"
                  type="product"
                  label="Primary Product Image *"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Images</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {product.images.map((img, index) => (
                    <div key={index} className="relative group">
                      <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-32 object-contain rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <GalleryImageUploader
                  onAdd={(url) => addGalleryImage(url)}
                  folder="products"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Videos & GIFs</label>
                <div className="space-y-4">
                  {product.media.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                        {item.type === 'video' ? <Video size={20} /> : <ImageIcon size={20} />}
                      </div>
                      <div className="flex-1 truncate">
                        <p className="font-medium text-gray-900 capitalize">{item.type}</p>
                        <p className="text-sm text-gray-500 truncate">{item.url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMedia(index)}
                        className="p-2 text-red-500 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter YouTube or video URL:');
                        if (url) addMedia('video', url);
                      }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Video size={18} />
                      Add Video
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter GIF URL:');
                        if (url) addMedia('gif', url);
                      }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <ImageIcon size={18} />
                      Add GIF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rich Description Tab */}
          {activeTab === 'description' && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  💡 <strong>Tip:</strong> Create engaging product descriptions with images, videos, feature boxes, and call-to-action buttons to motivate customers!
                </p>
                
                {/* Toolbar */}
                <div className="flex flex-wrap gap-1 p-2 bg-white rounded-lg border mb-3">
                  <button type="button" onClick={() => execCommand('bold')} className="p-2 hover:bg-gray-100 rounded" title="Bold">
                    <Bold size={18} />
                  </button>
                  <button type="button" onClick={() => execCommand('italic')} className="p-2 hover:bg-gray-100 rounded" title="Italic">
                    <Italic size={18} />
                  </button>
                  <button type="button" onClick={() => execCommand('underline')} className="p-2 hover:bg-gray-100 rounded" title="Underline">
                    <Underline size={18} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-2 hover:bg-gray-100 rounded" title="Bullet List">
                    <List size={18} />
                  </button>
                  <button type="button" onClick={() => execCommand('insertOrderedList')} className="p-2 hover:bg-gray-100 rounded" title="Numbered List">
                    <ListOrdered size={18} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button type="button" onClick={() => execCommand('justifyLeft')} className="p-2 hover:bg-gray-100 rounded" title="Align Left">
                    <AlignLeft size={18} />
                  </button>
                  <button type="button" onClick={() => execCommand('justifyCenter')} className="p-2 hover:bg-gray-100 rounded" title="Align Center">
                    <AlignCenter size={18} />
                  </button>
                  <button type="button" onClick={() => execCommand('justifyRight')} className="p-2 hover:bg-gray-100 rounded" title="Align Right">
                    <AlignRight size={18} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button type="button" onClick={insertImage} className="p-2 hover:bg-gray-100 rounded text-blue-600" title="Insert Image">
                    <ImageIcon size={18} />
                  </button>
                  <button type="button" onClick={insertVideo} className="p-2 hover:bg-gray-100 rounded text-red-600" title="Insert Video">
                    <Video size={18} />
                  </button>
                  <button type="button" onClick={insertGif} className="p-2 hover:bg-gray-100 rounded text-purple-600" title="Insert GIF">
                    <Sparkles size={18} />
                  </button>
                  <div className="w-px bg-gray-300 mx-1"></div>
                  <button type="button" onClick={insertCallout} className="p-2 hover:bg-gray-100 rounded text-green-600" title="Insert Callout Box">
                    <Type size={18} />
                  </button>
                  <button type="button" onClick={insertFeatureBox} className="p-2 hover:bg-gray-100 rounded text-orange-600" title="Insert Feature Grid">
                    <FileText size={18} />
                  </button>
                </div>

                {/* Quick Insert Buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={insertCallout}
                    className="text-xs px-3 py-1.5 bg-green-100 text-green-700 rounded-full hover:bg-green-200"
                  >
                    ✨ Highlight Box
                  </button>
                  <button
                    type="button"
                    onClick={insertFeatureBox}
                    className="text-xs px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200"
                  >
                    📦 Feature Grid
                  </button>
                  <button
                    type="button"
                    onClick={() => insertMedia(`<div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 15px 0; border-radius: 0 8px 8px 0;"><strong>🔥 Limited Time Offer!</strong><br/>Get free installation worth ₹999</div>`)}
                    className="text-xs px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200"
                  >
                    🔥 Offer Banner
                  </button>
                  <button
                    type="button"
                    onClick={() => insertMedia(`<a href="tel:+919887068888" style="display: inline-block; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 15px 30px; border-radius: 10px; text-decoration: none; font-weight: bold; margin: 15px 0;">📞 Call Now - Free Consultation</a>`)}
                    className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                  >
                    📞 CTA Button
                  </button>
                </div>
              </div>

              {/* Rich Text Editor */}
              <div
                ref={editorRef}
                contentEditable
                className="min-h-[400px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.rich_description) }}
                onInput={(e) => updateField('rich_description', e.currentTarget.innerHTML)}
                style={{ outline: 'none' }}
              />

              <p className="text-sm text-gray-500">
                You can add images, videos, GIFs, and styled boxes directly in the description to make it engaging!
              </p>
            </div>
          )}

          {/* Specifications Tab */}
          {activeTab === 'specs' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Purification Technology</label>
                <input
                  type="text"
                  value={product.specifications.purification}
                  onChange={(e) => updateSpecification('purification', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., RO + UV + UF + TDS Control"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Storage Capacity</label>
                <input
                  type="text"
                  value={product.specifications.capacity}
                  onChange={(e) => updateSpecification('capacity', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 8 Liters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Warranty</label>
                <input
                  type="text"
                  value={product.specifications.warranty}
                  onChange={(e) => updateSpecification('warranty', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 1 Year Comprehensive"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Power Consumption</label>
                <input
                  type="text"
                  value={product.specifications.powerConsumption}
                  onChange={(e) => updateSpecification('powerConsumption', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 60 Watts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Storage Type</label>
                <input
                  type="text"
                  value={product.specifications.storageType}
                  onChange={(e) => updateSpecification('storageType', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Food-grade ABS Plastic Tank"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dimensions</label>
                <input
                  type="text"
                  value={product.specifications.dimensions}
                  onChange={(e) => updateSpecification('dimensions', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 40 x 25 x 50 cm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                <input
                  type="text"
                  value={product.specifications.weight}
                  onChange={(e) => updateSpecification('weight', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 8.5 kg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Installation</label>
                <select
                  value={product.specifications.installation}
                  onChange={(e) => updateSpecification('installation', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Free">Free Installation</option>
                  <option value="Paid">Paid Installation</option>
                  <option value="Self">Self Installation</option>
                </select>
              </div>
            </div>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                <input
                  type="text"
                  value={product.sku}
                  onChange={(e) => updateField('sku', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., KENT-GP-8L"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={product.meta_title}
                  onChange={(e) => updateField('meta_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="SEO optimized title for search engines"
                />
                <p className="text-sm text-gray-500 mt-1">{product.meta_title?.length || 0}/60 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                <textarea
                  value={product.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  placeholder="SEO optimized description for search engines"
                />
                <p className="text-sm text-gray-500 mt-1">{product.meta_description?.length || 0}/160 characters</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={Array.isArray(product.tags) ? product.tags.join(', ') : product.tags}
                  onChange={(e) => updateField('tags', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., RO, water purifier, Kent, 8L"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords (comma separated)</label>
                <input
                  type="text"
                  value={Array.isArray(product.meta_keywords) ? product.meta_keywords.join(', ') : product.meta_keywords}
                  onChange={(e) => updateField('meta_keywords', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., buy kent ro jaipur, water purifier price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">OG / Social Share Image URL</label>
                <input
                  type="text"
                  value={product.og_image || ''}
                  onChange={(e) => updateField('og_image', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave blank to use primary product image"
                  data-testid="product-og-image"
                />
                <p className="text-sm text-gray-500 mt-1">Optional. Used for Facebook, WhatsApp, Twitter previews. Recommended: 1200x630px</p>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default ProductEditor;
